import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import {
  format,
  startOfDay,
  endOfDay,
  subDays,
  isToday,
} from "date-fns";
import { Router } from "@vaadin/router";
import "@awesome.me/webawesome/dist/components/button/button.js";
import "@awesome.me/webawesome/dist/components/icon/icon.js";
import "@awesome.me/webawesome/dist/components/details/details.js";
import "@awesome.me/webawesome/dist/components/dialog/dialog.js";
import "@awesome.me/webawesome/dist/components/textarea/textarea.js";
import "@awesome.me/webawesome/dist/components/input/input.js";
import "@awesome.me/webawesome/dist/components/spinner/spinner.js";
import "../components/app-shell";
import "../components/entry-card";
import { getDb } from "../db";
import { getActiveDb } from "../db/active-db";
import { monitoredUserSelectionService } from "../services/monitored-user-selection";
import type { DebugRenderable } from "../services/debug/debug-service";
import { saveDayNote, saveCgmReading } from "../db/operations";
import { syncService, type SyncState } from "../services/sync";
import { authService } from "../services/auth";
import { selectedDateService } from "../services/selected-date";
import type { LogEntry, CgmReading, DayNote, UserSettings } from "../types";
import {
  PERIOD_TAG_INFO,
  getQATotal,
  getBITotal,
  calculateIOB,
  getTimestampMs,
  DEFAULT_SETTINGS,
} from "../types";
import { getMissingSettings } from "../config/dafne";
import {
  analyzePatterns,
  generatePredictions,
  getCurrentPeriod,
  type PatternResult,
  type PredictionResult,
  type PredictionHistorySupportContext,
} from "../utils/pattern-analysis";
import { analyzeDafnePatterns } from "../utils/dafne-diary-analysis";

@customElement("home-page")
export class HomePage extends LitElement implements DebugRenderable {
  @state() private currentDate = selectedDateService.date;
  @state() private userName = "";
  private _selectedDateUnsubscribe: (() => void) | null = null;
  @state() private entries: LogEntry[] = [];
  @state() private cgmReadings: CgmReading[] = [];
  @state() private hasCgmData = false;
  @state() private predictions: PredictionResult[] = [];
  @state() private historicalPatterns: PatternResult[] = [];
  @state() private dayNotes: DayNote[] = [];
  @state() private showNoteDialog = false;
  @state() private noteText = "";
  @state() private savingNote = false;
  @state() private isInitializing = true;
  @state() private isMonitorView = false;

  /** Auto-refresh interval (5 minutes) */
  private _refreshInterval: ReturnType<typeof setInterval> | null = null;
  private static readonly REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
  private _liveGlHandler = this._handleLiveGl.bind(this);
  private _syncUnsubscribe: (() => void) | null = null;
  private _monitorSelectionUnsubscribe: (() => void) | null = null;

  static styles = css`
    :host {
      display: block;
    }

    .date-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1rem;
    }

    .date-nav {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .date-nav wa-button::part(base) {
      min-width: 36px;
      padding: 0.25rem;
    }

    .date-info h2 {
      margin: 0 0 0.25rem 0;
      font-size: 1.125rem;
      font-weight: 600;
    }

    .date-info p {
      margin: 0;
      color: #6c757d;
      font-size: 0.875rem;
    }

    .date-picker-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 0.75rem;
      background: #f8f9fa;
      border: 1px solid #dee2e6;
      border-radius: 0.5rem;
      cursor: pointer;
      font-size: 0.875rem;
      color: #495057;
      transition:
        background-color 0.15s,
        border-color 0.15s;
    }

    .date-picker-btn:hover {
      background: #e9ecef;
      border-color: #adb5bd;
    }

    .date-picker-btn:active {
      background: #dee2e6;
    }

    .date-picker-btn wa-icon {
      font-size: 1rem;
      color: #0d6efd;
    }

    .entries-section {
      padding: 0 1rem;
    }

    .entries-section h3 {
      font-size: 1.125rem;
      font-weight: 600;
      color: #495057;
      text-transform: uppercase;
      margin: 0 0 0.75rem;
    }

    .entries-container {
      padding-bottom: 6rem;
    }


    .empty-state {
      text-align: center;
      padding: 3rem 1rem;
      color: #6c757d;
    }

    /* Day Notes Section */
    .day-notes-section {
      margin-top: 1rem;
      padding: 0.75rem;
      background: #f8f9fa;
      border-radius: 0.5rem;
    }

    .day-notes-section h4 {
      font-size: 0.7rem;
      font-weight: 600;
      color: #6c757d;
      text-transform: uppercase;
      margin: 0 0 0.5rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .day-note {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      padding: 0.5rem;
      background: #fff;
      border-radius: 0.375rem;
      border: 1px solid #e9ecef;
      border-left: 3px solid #0d6efd;
      margin-bottom: 0.5rem;
    }

    .day-note-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.7rem;
      color: #6c757d;
    }

    .day-note-header .note-date {
      margin-left: auto;
    }

    .day-note-content {
      font-size: 0.85rem;
      color: #212529;
      white-space: pre-wrap;
    }

    .add-note-btn {
      font-size: 0.75rem;
    }

    .note-dialog-date {
      margin: 0 0 1rem;
      color: #6c757d;
      font-size: 0.875rem;
    }

    @media (min-width: 1024px) {
      .entries-container {
        max-width: 600px;
        padding-bottom: 2rem;
      }
    }

    .graph-section {
      padding: 0 1rem;
      margin-bottom: 1rem;
    }

    .graph-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.5rem;
    }

    .graph-header h3 {
      margin: 0;
      font-size: 0.875rem;
      font-weight: 500;
      color: #495057;
    }

    .graph-header a {
      font-size: 0.75rem;
      color: #0d6efd;
      text-decoration: none;
    }

    /* Summary Section */
    .summary-section {
      padding: 0 1rem;
      margin-bottom: 1rem;
    }

    .summary-cards {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 0.5rem;
      max-width: 28rem;
      margin-inline: auto;
    }

    .summary-card {
      background: var(--color-card, #d9efff);
      border-radius: var(--radius-stat, 1rem);
      padding: 0.5rem;
      text-align: center;
      aspect-ratio: 1 / 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.25rem;
    }

    .summary-card.iob {
      background: var(--color-card, #d9efff);
    }

    .summary-card .value {
      font-size: 1.75rem;
      font-weight: 700;
      line-height: 1.05;
      color: #212529;
    }

    .summary-card .label {
      font-size: 0.625rem;
      color: #6c757d;
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }

    .summary-card .value.good {
      color: #198754;
    }

    .summary-card .value.warning {
      color: #fd7e14;
    }

    .summary-card .value.neutral {
      color: #6c757d;
    }

    .graph-links {
      display: flex;
      gap: 0.75rem;
    }

    /* Health Recommendations section (predictions) */
    .predictions-section {
      padding: 0 1rem 1rem;
    }

    .predictions-section wa-details {
      --color-accent: #4fb39b;
    }

    .predictions-section wa-details::part(base) {
      background: #fff;
      border: 1px solid #afdcb4;
      border-radius: 0.9rem;
      box-shadow: 0 1px 3px rgba(79, 179, 155, 0.08);
    }

    .predictions-section wa-details::part(header) {
      padding: 0.75rem 1rem;
      font-weight: 600;
      color: #1f2d3d;
    }

    .predictions-section wa-details::part(content) {
      padding: 0 1rem 1rem;
    }

    .predictions-section .recommendations-summary {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.95rem;
    }

    .predictions-section .recommendations-summary wa-icon {
      color: #4fb39b;
      font-size: 1.1rem;
    }

    .predictions-section .recommendations-empty-bar {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 1rem;
      background: #fff;
      border: 1px solid #afdcb4;
      border-radius: 0.9rem;
      color: #6c757d;
      font-size: 0.9rem;
      box-shadow: 0 1px 3px rgba(79, 179, 155, 0.08);
    }

    .predictions-section .recommendations-empty-bar wa-icon {
      color: #4fb39b;
      font-size: 1.1rem;
    }

    .predictions-section .recommendations-count {
      margin-left: auto;
      background: #afdcb4;
      color: #1f2d3d;
      font-size: 0.7rem;
      font-weight: 700;
      padding: 0.1rem 0.5rem;
      border-radius: 999px;
    }

    .prediction-cards {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .prediction-card {
      background: #fff;
      border: 1px solid #e9ecef;
      border-radius: 0.5rem;
      padding: 0.75rem;
    }

    .prediction-card.high-risk {
      background: #fff5f5;
      border-color: #feb2b2;
    }

    .prediction-card.medium-risk {
      background: #fffaf0;
      border-color: #fbd38d;
    }

    .prediction-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.5rem;
    }

    .prediction-type {
      font-weight: 600;
      font-size: 0.85rem;
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }

    .prediction-likelihood {
      font-size: 0.65rem;
      padding: 0.15rem 0.4rem;
      border-radius: 0.25rem;
      font-weight: 600;
      text-transform: uppercase;
    }

    .prediction-likelihood.high {
      background: #dc3545;
      color: white;
    }

    .prediction-likelihood.medium {
      background: #fd7e14;
      color: white;
    }

    .prediction-likelihood.low {
      background: #6c757d;
      color: white;
    }

    .prediction-likelihood.info {
      background: #17a2b8;
      color: white;
    }

    .prediction-card.info-card {
      background: #f0f9ff;
      border-color: #bee3f8;
    }

    .prediction-body {
      font-size: 0.8rem;
      color: #495057;
    }

    .prediction-source {
      display: inline-flex;
      align-items: center;
      font-size: 0.62rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.02em;
      padding: 0.12rem 0.4rem;
      border-radius: 999px;
      margin-bottom: 0.35rem;
    }

    .prediction-source.today {
      background: #e2e3e5;
      color: #343a40;
    }

    .prediction-source.history {
      background: #d1ecf1;
      color: #0c5460;
    }

    .prediction-based-on {
      margin-bottom: 0.25rem;
    }

    .prediction-support {
      margin-top: 0.25rem;
      color: #6c757d;
      font-size: 0.7rem;
    }

    .prediction-timeframe {
      color: #6c757d;
      font-size: 0.75rem;
    }

    .prediction-validity {
      color: #6c757d;
      font-size: 0.7rem;
      font-style: italic;
    }

    .prediction-action {
      margin-top: 0.5rem;
      padding: 0.5rem;
      background: #f8f9fa;
      border-radius: 0.375rem;
      border-left: 3px solid #0d6efd;
      font-size: 0.75rem;
    }

    .no-predictions {
      text-align: center;
      padding: 0.75rem;
      color: #6c757d;
      background: #f8f9fa;
      border-radius: 0.375rem;
      font-size: 0.8rem;
    }

    .greeting {
      padding: 1rem 1rem 0;
      font-size: 2rem;
      font-weight: 700;
      color: #4fb39b;
      line-height: 1.2;
    }

    .greeting .name {
      display: block;
      color: #1f2d3d;
    }
  `;

  private async _redirectToSettingsIfIncomplete(): Promise<boolean> {
    try {
      const storedSettings = await getDb().settings.get("settings");
      const missing = getMissingSettings(storedSettings || DEFAULT_SETTINGS);
      if (missing.length > 0) {
        Router.go("/settings?required=1&from=today");
        return true;
      }
    } catch (error) {
      console.error("Failed to validate required settings:", error);
    }
    return false;
  }

  async connectedCallback() {
    super.connectedCallback();
    this.isInitializing = true;
    this.isMonitorView = monitoredUserSelectionService.isMonitorOnly;

    // Sync with the shared selected-date store (header date stepper)
    this.currentDate = selectedDateService.date;
    this._selectedDateUnsubscribe = selectedDateService.subscribe((date) => {
      if (date.getTime() === this.currentDate.getTime()) return;
      this.currentDate = date;
      this._loadAllData();
    });

    this._monitorSelectionUnsubscribe =
      monitoredUserSelectionService.subscribe(() => {
        this.isMonitorView = monitoredUserSelectionService.isMonitorOnly;
        this._loadAllData();
      });

    if (this.isMonitorView) {
      // Monitor-only: wait for data to be ready in local DB
      await monitoredUserSelectionService.whenLoaded();
      if (
        !monitoredUserSelectionService.selected
      ) {
        Router.go("/monitoring");
        return;
      }
      await monitoredUserSelectionService.whenDataReady();
      this.isInitializing = false;
      await this._loadAllData();
    } else {
      // Listen for live GL entries from header button
      window.addEventListener(
        "live-gl-entered",
        this._liveGlHandler as unknown as EventListener,
      );

      // CRITICAL: Wait for initial sync to complete before checking settings
      await this._waitForInitialSync();

      if (await this._redirectToSettingsIfIncomplete()) {
        this.isInitializing = false;
        return;
      }

      this.isInitializing = false;

      await this._loadAllData();
    }

    // Set up auto-refresh every 5 minutes
    this._refreshInterval = setInterval(async () => {
      if (isToday(this.currentDate)) {
        await this._loadAllData();
      }
    }, HomePage.REFRESH_INTERVAL_MS);
  }

  private async _loadAllData() {
    if (this.isMonitorView) {
      this.userName =
        monitoredUserSelectionService.selected?.displayName || "";
      // Use the same local-DB methods — getActiveDb() returns monitored DB
      await this._loadEntries();
      await this._loadCgmReadings();
      await this._loadDayNotes();
      await this._loadPredictions();
    } else {
      await this._loadEntries();
      await this._loadCgmReadings();
      await this._loadDayNotes();
      await this._loadPredictions();
      await this._loadUserName();
    }
  }

  /**
   * Wait for initial sync to complete (or timeout/go offline)
   * This ensures we have pulled server data before making any decisions
   */
  private async _waitForInitialSync(): Promise<void> {
    // Check if already complete
    const currentState = syncService.state;
    if (currentState.initialSyncComplete || currentState.isConfirmedFreshUser) {
      return;
    }

    // If offline, skip waiting (user can work offline)
    if (!navigator.onLine) {
      return;
    }

    // Trigger sync if not already in progress
    if (currentState.status === "idle") {
      syncService.checkAndSync();
    }

    // Wait for sync to complete with timeout
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        // Timeout after 15 seconds - let user proceed
        console.warn(
          "[HomePage] Initial sync timeout - proceeding without sync",
        );
        if (this._syncUnsubscribe) {
          this._syncUnsubscribe();
          this._syncUnsubscribe = null;
        }
        resolve();
      }, 15000);

      this._syncUnsubscribe = syncService.subscribe((state: SyncState) => {
        if (
          state.initialSyncComplete ||
          state.isConfirmedFreshUser ||
          state.status === "error" ||
          state.status === "offline"
        ) {
          clearTimeout(timeout);
          if (this._syncUnsubscribe) {
            this._syncUnsubscribe();
            this._syncUnsubscribe = null;
          }
          resolve();
        }
      });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Remove live GL listener
    window.removeEventListener(
      "live-gl-entered",
      this._liveGlHandler as unknown as EventListener,
    );
    // Unsubscribe from shared selected date
    if (this._selectedDateUnsubscribe) {
      this._selectedDateUnsubscribe();
      this._selectedDateUnsubscribe = null;
    }
    // Clean up sync subscription
    if (this._syncUnsubscribe) {
      this._syncUnsubscribe();
      this._syncUnsubscribe = null;
    }
    // Clean up monitor selection subscription
    if (this._monitorSelectionUnsubscribe) {
      this._monitorSelectionUnsubscribe();
      this._monitorSelectionUnsubscribe = null;
    }
    // Clean up refresh interval
    if (this._refreshInterval) {
      clearInterval(this._refreshInterval);
      this._refreshInterval = null;
    }
  }

  private async _handleLiveGl(
    e: CustomEvent<{ glucose: number; timestamp: Date }>,
  ) {
    const { glucose, timestamp } = e.detail;
    // Save to database as a manual CGM reading
    await saveCgmReading({
      timestamp,
      glucose,
      type: "manual",
      source: "manual",
      createdAt: new Date(),
    });
    // Reload CGM readings and predictions
    await this._loadCgmReadings();
    await this._loadPredictions();
  }

  private async _loadEntries() {
    const dayStart = startOfDay(this.currentDate).getTime();
    const dayEnd = endOfDay(this.currentDate).getTime();

    console.log("[HomePage] Loading entries for date:", {
      currentDate: this.currentDate,
      currentDateISO: this.currentDate.toISOString(),
      currentDateLocal: this.currentDate.toLocaleString(),
      dayStartUTC: new Date(dayStart).toISOString(),
      dayStartLocal: new Date(dayStart).toLocaleString(),
      dayEndUTC: new Date(dayEnd).toISOString(),
      dayEndLocal: new Date(dayEnd).toLocaleString(),
    });

    try {
      // Load all entries and filter in JS
      // IndexedDB date comparisons are unreliable when timestamps
      // may be stored as strings (from server) vs Date objects (local)
      const allEntries = await getActiveDb().entries.toArray();

      // Helper to get timestamp as number
      const getTs = (t: Date | string | number | unknown): number => {
        if (t instanceof Date) return t.getTime();
        if (typeof t === "string") return new Date(t).getTime();
        if (typeof t === "number") return t;
        return new Date(t as string).getTime();
      };

      // Debug: log entries that are close to midnight
      const debugStart = dayStart - 2 * 60 * 60 * 1000; // 2 hours before
      const debugEnd = dayStart + 2 * 60 * 60 * 1000; // 2 hours after midnight
      const nearMidnight = allEntries.filter((e) => {
        const ts = getTs(e.timestamp);
        return ts >= debugStart && ts <= debugEnd;
      });
      if (nearMidnight.length > 0) {
        console.log(
          "Entries near midnight:",
          nearMidnight.map((e) => ({
            id: e.id,
            timestamp: e.timestamp,
            tsValue: getTs(e.timestamp),
            localTime: new Date(getTs(e.timestamp)).toLocaleString(),
            _deletedAt: (e as any)._deletedAt,
            periodTag: e.periodTag,
          })),
        );
        console.log("Day bounds:", {
          dayStart,
          dayEnd,
          dayStartLocal: new Date(dayStart).toLocaleString(),
          dayEndLocal: new Date(dayEnd).toLocaleString(),
        });
      }

      // Debug: log all entries that match the day filter
      const dayMatchingEntries = allEntries.filter((e) => {
        const ts = getTs(e.timestamp);
        return ts >= dayStart && ts <= dayEnd;
      });
      console.log(
        `Home page: ${allEntries.length} total entries, ${dayMatchingEntries.length} match day (${new Date(dayStart).toLocaleDateString()})`,
      );

      // Log ALL recent entries to see what's happening
      const recentEntries = allEntries
        .map((e) => ({
          id: e.id.substring(0, 8),
          rawTimestamp: e.timestamp,
          tsMs: getTs(e.timestamp),
          utc: new Date(getTs(e.timestamp)).toISOString(),
          local: new Date(getTs(e.timestamp)).toLocaleString(),
          inRange:
            getTs(e.timestamp) >= dayStart && getTs(e.timestamp) <= dayEnd,
          deleted: !!(e as any)._deletedAt,
        }))
        .sort((a, b) => b.tsMs - a.tsMs)
        .slice(0, 10);
      console.log("Recent 10 entries:", recentEntries);

      // SPECIFIC DEBUG: Find the 00:38 entry (periodTag 3AM, gl around 5.4)
      const suspectEntries = allEntries.filter((e) => {
        const gl = e.bloodGlucose;
        const period = e.periodTag;
        // Look for 3AM entries or GL 5.4
        return period === "3AM" || (gl && gl >= 5.3 && gl <= 5.5);
      });
      console.log(
        "🔍 3AM/GL5.4 entries found:",
        suspectEntries.map((e) => ({
          id: e.id.substring(0, 8),
          periodTag: e.periodTag,
          gl: e.bloodGlucose,
          rawTimestamp: e.timestamp,
          timestampType: typeof e.timestamp,
          tsMs: getTs(e.timestamp),
          local: new Date(getTs(e.timestamp)).toLocaleString(),
          inDayRange:
            getTs(e.timestamp) >= dayStart && getTs(e.timestamp) <= dayEnd,
          dayStart,
          dayEnd,
        })),
      );

      this.entries = allEntries
        .filter((e) => {
          // Skip deleted entries
          if ((e as any)._deletedAt) return false;
          const ts = getTs(e.timestamp);
          return ts >= dayStart && ts <= dayEnd;
        })
        .sort((a, b) => getTs(a.timestamp) - getTs(b.timestamp));
    } catch (error) {
      console.error("Failed to load entries:", error);
    }
  }

  private async _loadCgmReadings() {
    try {
      const db = getActiveDb();
      const count = await db.cgmReadings.count();
      this.hasCgmData = count > 0;

      const dayStart = startOfDay(this.currentDate).getTime();
      const dayEnd = endOfDay(this.currentDate).getTime();

      // Filter in JS because IndexedDB date comparisons can be unreliable
      const allReadings = await db.cgmReadings.toArray();

      // Helper to get timestamp as number
      const getTs = (t: Date | string | number | unknown): number => {
        if (t instanceof Date) return t.getTime();
        if (typeof t === "string") return new Date(t).getTime();
        if (typeof t === "number") return t;
        return new Date(t as string).getTime();
      };

      // Get day's CGM readings
      let dayReadings = allReadings
        .filter((r) => {
          const ts = getTs(r.timestamp);
          return ts >= dayStart && ts <= dayEnd;
        })
        .sort((a, b) => getTs(a.timestamp) - getTs(b.timestamp));

      // Also include DAFNE log entries with bloodGlucose that don't overlap with CGM readings
      const allEntries = await db.entries.toArray();
      for (const entry of allEntries) {
        if (
          entry.bloodGlucose !== undefined &&
          entry.bloodGlucose > 0 &&
          !(entry as any)._deletedAt
        ) {
          const entryTs = getTs(entry.timestamp);
          if (entryTs >= dayStart && entryTs <= dayEnd) {
            // Check if there's already a reading within 5 minutes
            const hasNearbyReading = dayReadings.some((r) => {
              const ts = getTs(r.timestamp);
              return Math.abs(ts - entryTs) < 5 * 60 * 1000;
            });

            if (!hasNearbyReading) {
              dayReadings.push({
                id: `entry-${entry.id}`,
                timestamp: entry.timestamp,
                glucose: entry.bloodGlucose,
                type: "fingerstick",
                source: "manual",
                createdAt: entry.createdAt,
              });
            }
          }
        }
      }

      // Re-sort after adding entries
      this.cgmReadings = dayReadings.sort(
        (a, b) => getTs(a.timestamp) - getTs(b.timestamp),
      );
      this.hasCgmData = this.hasCgmData || this.cgmReadings.length > 0;
    } catch (error) {
      console.error("Failed to load CGM readings:", error);
    }
  }

  private async _loadPredictions() {
    if (!isToday(this.currentDate)) {
      // Only show predictions for today
      this.predictions = [];
      return;
    }

    try {
      // Get all historical data for pattern analysis
      const db = getActiveDb();
      const [allReadings, allEntries, storedSettings] = await Promise.all([
        db.cgmReadings.toArray(),
        db.entries.toArray(),
        db.settings.get("settings") as Promise<UserSettings | undefined>,
      ]);

      // Get user's correction factor (how much 1u insulin drops GL)
      const correctionFactor =
        storedSettings?.correctionFactor ??
        DEFAULT_SETTINGS.correctionFactor ??
        2.5;

      // Get yesterday's entries for comparison
      const yesterday = subDays(new Date(), 1);
      const yesterdayStart = startOfDay(yesterday).getTime();
      const yesterdayEnd = endOfDay(yesterday).getTime();
      const yesterdayEntries = allEntries.filter((e) => {
        const ts =
          e.timestamp instanceof Date
            ? e.timestamp.getTime()
            : new Date(e.timestamp).getTime();
        return ts >= yesterdayStart && ts <= yesterdayEnd;
      });

      // Analyze patterns from recent history
      this.historicalPatterns = analyzePatterns({
        cgmReadings: allReadings,
        entries: allEntries,
        daysToAnalyze: 7,
        targetLow: 4,
        targetHigh: 10,
      });

      const diarySupport = analyzeDafnePatterns(allEntries, 7);
      const historySupport: PredictionHistorySupportContext = {
        daysAnalyzed: 7,
        daysWithDiaryData: diarySupport.analysisWindow.daysWithData,
        usedDiaryAssessment: diarySupport.analysisWindow.daysWithData >= 2,
      };

      // Generate predictions based on today's entries, CGM, patterns, and yesterday's data
      // Manual GL entries are automatically included in cgmReadings from the database
      this.predictions = generatePredictions(
        this.entries,
        this.cgmReadings,
        this.historicalPatterns,
        getCurrentPeriod(),
        yesterdayEntries,
        historySupport,
        correctionFactor,
      );
    } catch (error) {
      console.error("Failed to load predictions:", error);
    }
  }

  private async _loadDayNotes() {
    const dateKey = format(this.currentDate, "yyyy-MM-dd");
    try {
      const db = getActiveDb();
      const allNotes = await db.dayNotes.toArray();
      this.dayNotes = allNotes
        .filter((n) => n.date === dateKey)
        .sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
    } catch (error) {
      console.error("Failed to load day notes:", error);
      this.dayNotes = [];
    }
  }

  private _openAddNoteDialog() {
    this.noteText = "";
    this.showNoteDialog = true;
  }

  private _closeNoteDialog() {
    this.showNoteDialog = false;
    this.noteText = "";
  }

  private async _handleSaveNote() {
    if (!this.noteText.trim()) return;

    const authState = await authService.getCurrentUser();
    if (!authState.isAuthenticated || !authState.userId) return;

    this.savingNote = true;
    try {
      await saveDayNote({
        date: format(this.currentDate, "yyyy-MM-dd"),
        type: "personal",
        content: this.noteText.trim(),
        authorId: authState.userId,
        authorName: authState.displayName || authState.email || "Me",
      });

      // Refresh day notes
      await this._loadDayNotes();
      this._closeNoteDialog();
    } catch (error) {
      console.error("Failed to save note:", error);
    } finally {
      this.savingNote = false;
    }
  }




  private async _loadUserName(): Promise<void> {
    try {
      const stored = (await getActiveDb().settings.get("settings")) as
        | UserSettings
        | undefined;
      const auth = authService.state;
      const fromAuth = auth.displayName || "";
      const fromEmail = auth.email ? auth.email.split("@")[0] : "";
      this.userName =
        stored?.nickname?.trim() || fromAuth || fromEmail || "";
    } catch {
      this.userName = "";
    }
  }

  private _greeting(): string {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    if (h < 22) return "Good evening";
    return "Good night";
  }

  private _calculateActiveIOB(): number {
    const now = new Date();
    let totalIOB = 0;

    for (const entry of this.entries) {
      const qaTotal = getQATotal(entry);
      if (qaTotal) {
        const hoursElapsed =
          (now.getTime() - getTimestampMs(entry.timestamp)) / (1000 * 60 * 60);
        if (hoursElapsed >= 0 && hoursElapsed <= 5) {
          totalIOB += calculateIOB(qaTotal, hoursElapsed);
        }
      }
    }

    return Math.round(totalIOB * 10) / 10;
  }

  private _renderSummary() {
    // Calculate totals for the day
    let totalCarbs = 0;
    let totalQA = 0;
    let totalBI = 0;

    for (const entry of this.entries) {
      if (entry.carbs) totalCarbs += entry.carbs;
      const qa = getQATotal(entry);
      if (qa) totalQA += qa;
      const bi = getBITotal(entry);
      if (bi) totalBI += bi;
    }

    const activeIOB = this._calculateActiveIOB();

    return html`
      <div class="summary-section">
        <div class="summary-cards">
          <div class="summary-card iob">
            <div class="value ${activeIOB > 0 ? "warning" : "neutral"}">
              ${activeIOB}u
            </div>
            <div class="label">IOB</div>
          </div>
          <div class="summary-card">
            <div class="value">${totalCarbs}g</div>
            <div class="label">Carbs</div>
          </div>
          <div class="summary-card">
            <div class="value">${totalQA}u</div>
            <div class="label">Quick</div>
          </div>
          <div class="summary-card">
            <div class="value">${totalBI > 0 ? `${totalBI}u` : "-"}</div>
            <div class="label">Long</div>
          </div>
        </div>
      </div>
    `;
  }

  private _renderPredictions() {
    // Only show predictions for today
    if (!isToday(this.currentDate)) {
      return "";
    }

    const getPredictionIcon = (type: PredictionResult["type"]): string => {
      switch (type) {
        case "hypo-risk":
          return "⬇️";
        case "hyper-risk":
          return "⬆️";
        case "stacking":
          return "⚠️";
        case "bg-trajectory":
          return "📈";
        case "missing-long":
          return "💉";
        case "exercise-effect":
          return "🏃";
        case "correction-effect":
          return "📉";
        case "carb-effect":
          return "🍞";
        case "yesterday-compare":
          return "📅";
        case "info":
          return "ℹ️";
        default:
          return "💡";
      }
    };

    const getPredictionLabel = (type: PredictionResult["type"]): string => {
      switch (type) {
        case "hypo-risk":
          return "Hypo Risk";
        case "hyper-risk":
          return "High GL Risk";
        case "stacking":
          return "Insulin Stacking";
        case "bg-trajectory":
          return "GL Trend";
        case "missing-long":
          return "Long Insulin";
        case "exercise-effect":
          return "Exercise Effect";
        case "correction-effect":
          return "Correction Active";
        case "carb-effect":
          return "Carb Absorption";
        case "yesterday-compare":
          return "Yesterday";
        case "info":
          return "Info";
        default:
          return "Alert";
      }
    };

    const getPredictionSourceLabel = (pred: PredictionResult): string => {
      return pred.predictionSource === "history-supported"
        ? "History-supported"
        : "Today-only";
    };

    const formatPredictionTime = (pred: PredictionResult): string => {
      if (!pred.relevantFrom && !pred.validUntil) return "";
      const parts: string[] = [];
      if (pred.relevantFrom) {
        parts.push(`From ${format(pred.relevantFrom, "h:mm a")}`);
      }
      if (pred.validUntil) {
        const now = new Date();
        if (pred.validUntil > now) {
          parts.push(`until ${format(pred.validUntil, "h:mm a")}`);
        } else {
          parts.push(`(expired at ${format(pred.validUntil, "h:mm a")})`);
        }
      }
      return parts.join(" ");
    };

    // Filter out expired predictions
    const now = new Date();
    const activePredictions = this.predictions.filter((pred) => {
      if (!pred.validUntil) return true; // No expiry = always show
      return pred.validUntil > now;
    });

    if (activePredictions.length === 0) {
      return html`
        <div class="predictions-section">
          <div class="recommendations-empty-bar">
            <wa-icon name="heart-pulse"></wa-icon>
            <span>No health recommendations right now</span>
          </div>
        </div>
      `;
    }

    return html`
      <div class="predictions-section">
        <wa-details open>
          <div slot="summary" class="recommendations-summary">
            <wa-icon name="heart-pulse"></wa-icon>
            <span>Health Recommendations</span>
            <span class="recommendations-count"
              >${activePredictions.length}</span
            >
          </div>
          ${html`
              <div class="prediction-cards">
                ${activePredictions.map(
                  (pred) => html`
                    <div
                      class="prediction-card ${pred.likelihood === "high"
                        ? "high-risk"
                        : pred.likelihood === "medium"
                          ? "medium-risk"
                          : pred.likelihood === "info"
                            ? "info-card"
                            : ""}"
                    >
                      <div class="prediction-header">
                        <span class="prediction-type">
                          ${getPredictionIcon(pred.type)}
                          ${getPredictionLabel(pred.type)}
                        </span>
                        <span class="prediction-likelihood ${pred.likelihood}">
                          ${pred.likelihood}
                        </span>
                      </div>
                      <div class="prediction-body">
                        <div
                          class="prediction-source ${pred.predictionSource ===
                          "history-supported"
                            ? "history"
                            : "today"}"
                        >
                          ${getPredictionSourceLabel(pred)}
                        </div>
                        <div class="prediction-based-on">${pred.basedOn}</div>
                        ${pred.timeframe !== "comparison"
                          ? html`<div class="prediction-timeframe">
                              Timeframe: ${pred.timeframe}
                            </div>`
                          : ""}
                        ${pred.relevantFrom || pred.validUntil
                          ? html`<div class="prediction-validity">
                              ${formatPredictionTime(pred)}
                            </div>`
                          : ""}
                        ${pred.action
                          ? html`<div class="prediction-action">
                              ${pred.action}
                            </div>`
                          : ""}
                        ${pred.supportNote
                          ? html`<div class="prediction-support">
                              ${pred.supportNote}
                            </div>`
                          : ""}
                      </div>
                    </div>
                  `,
                )}
              </div>
            `}
        </wa-details>
      </div>
    `;
  }

  private _renderDayNotes() {
    return html`
      <div class="day-notes-section">
        <h4>
          <wa-icon name="book"></wa-icon>
          Day Notes
        </h4>
        ${this.dayNotes.map(
          (note) => html`
            <div class="day-note">
              <div class="day-note-header">
                <span>${note.authorName || "Me"}</span>
                <span class="note-date"
                  >${format(new Date(note.createdAt), "HH:mm")}</span
                >
              </div>
              <div class="day-note-content">${note.content}</div>
            </div>
          `,
        )}
        ${!this.isMonitorView
          ? html`<wa-button
              size="small"
              variant="text"
              class="add-note-btn"
              @click=${() => this._openAddNoteDialog()}
            >
              <wa-icon slot="prefix" name="circle-plus"></wa-icon>
              Add Note
            </wa-button>`
          : ""}
      </div>
    `;
  }

  /**
   * Generate markdown representation of current page state for debugging
   */
  renderMarkdown(): string {
    const lines: string[] = [];
    const dateStr = format(this.currentDate, "EEEE, d MMMM yyyy");
    const todayDate = isToday(this.currentDate);

    lines.push(`## ${todayDate ? "Today" : dateStr}`);
    lines.push("");

    // Summary totals
    let totalCarbs = 0;
    let totalQA = 0;
    let totalBI = 0;
    for (const entry of this.entries) {
      if (entry.carbs) totalCarbs += entry.carbs;
      const qa = getQATotal(entry);
      if (qa) totalQA += qa;
      const bi = getBITotal(entry);
      if (bi) totalBI += bi;
    }
    const activeIOB = this._calculateActiveIOB();

    lines.push("### Summary");
    lines.push("");
    lines.push(`- **IOB:** ${activeIOB}u`);
    lines.push(`- **Carbs:** ${totalCarbs}g`);
    lines.push(`- **Quick:** ${totalQA}u`);
    lines.push(`- **Long:** ${totalBI > 0 ? `${totalBI}u` : "-"}`);
    lines.push("");

    // CGM summary
    if (this.hasCgmData && this.cgmReadings.length > 0) {
      const values = this.cgmReadings.map((r) => r.glucose);
      const avg = values.reduce((a, b) => a + b, 0) / values.length;
      const min = Math.min(...values);
      const max = Math.max(...values);
      lines.push("### CGM Data");
      lines.push("");
      lines.push(`- **Readings:** ${this.cgmReadings.length}`);
      lines.push(`- **Average:** ${avg.toFixed(1)} mmol/L`);
      lines.push(`- **Range:** ${min.toFixed(1)} - ${max.toFixed(1)} mmol/L`);
      lines.push("");
    }

    // Predictions (only for today)
    if (todayDate && this.predictions.length > 0) {
      lines.push("### Predictions");
      lines.push("");
      for (const pred of this.predictions) {
        const likelihood =
          pred.likelihood === "high"
            ? "[HIGH]"
            : pred.likelihood === "medium"
              ? "[MED]"
              : "";
        lines.push(
          `- ${likelihood} **${pred.type}**: ${pred.timeframe} (${pred.basedOn})`,
        );
        if (pred.action) {
          lines.push(`  - Action: ${pred.action}`);
        }
      }
      lines.push("");
    }

    // Diary entries
    lines.push("### Diary Entries");
    lines.push("");
    if (this.entries.length === 0) {
      lines.push("No entries yet today.");
    } else {
      lines.push(
        "| Time | Period | GL | Carbs | Quick | Long | Ketones | Note |",
      );
      lines.push(
        "|------|--------|-----|-------|-------|------|---------|------|",
      );
      for (const entry of this.entries) {
        const time = format(new Date(getTimestampMs(entry.timestamp)), "HH:mm");
        const period = PERIOD_TAG_INFO[entry.periodTag].label;
        const gl = entry.bloodGlucose
          ? `${entry.bloodGlucose.toFixed(1)}`
          : "-";
        const carbs = entry.carbs ? `${entry.carbs}g` : "-";
        const qa = getQATotal(entry);
        const quick = qa !== undefined ? `${qa}u` : "-";
        const bi = getBITotal(entry);
        const long = bi ? `${bi}u` : "-";
        const ket =
          entry.ketones !== undefined ? `${entry.ketones.toFixed(1)}` : "-";
        const note = entry.notes ? entry.notes.substring(0, 30) : "-";
        lines.push(
          `| ${time} | ${period} | ${gl} | ${carbs} | ${quick} | ${long} | ${ket} | ${note} |`,
        );
      }
    }
    lines.push("");

    // Day notes
    if (this.dayNotes.length > 0) {
      lines.push("### Day Notes");
      lines.push("");
      for (const note of this.dayNotes) {
        lines.push(`- ${note.content}`);
      }
      lines.push("");
    }

    return lines.join("\n");
  }

  render() {
    // Show loading state while initializing (waiting for sync)
    if (this.isInitializing) {
      return html`
        <app-shell pageTitle="Today">
          <div
            style="display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 50vh; gap: 1rem; color: #6c757d;"
          >
            <wa-spinner style="font-size: 2rem;"></wa-spinner>
            <p>Loading your data...</p>
          </div>
        </app-shell>
      `;
    }

    return html`
      <app-shell pageTitle="Today">
        <header-date-stepper slot="header-center"></header-date-stepper>
        ${this.isMonitorView
          ? html`
              <div class="greeting">
                ${this.userName}
                <span class="name">Today</span>
              </div>
            `
          : isToday(this.currentDate)
            ? html`
                <div class="greeting">
                  ${this._greeting()},
                  ${this.userName
                    ? html`<span class="name">${this.userName}</span>`
                    : ""}
                </div>
              `
            : ""}
        ${this._renderSummary()}
        ${this._renderPredictions()}

        <div class="entries-section">
          <h3>Diary Entries</h3>
          <div class="entries-container">
            ${this.entries.length === 0
              ? html`
                  <div class="empty-state">
                    <p>
                      ${this.isMonitorView
                        ? "No entries for this day."
                        : html`No entries yet today.<br />Tap + to log your first entry.`}
                    </p>
                  </div>
                `
              : this.entries.map(
                  (e) => html`<entry-card
                    .entry=${e}
                    .allEntries=${this.entries}
                    ?hideEdit=${this.isMonitorView}
                  ></entry-card>`,
                )}
          </div>
          ${this._renderDayNotes()}
        </div>

        <!-- Add Note Dialog -->
        <wa-dialog
          label="Add Note"
          ?open=${this.showNoteDialog}
          @wa-request-close=${() => this._closeNoteDialog()}
        >
          <p class="note-dialog-date">
            Note for ${format(this.currentDate, "EEEE, d MMMM yyyy")}
          </p>
          <wa-textarea
            placeholder="Add your note about this day..."
            rows="4"
            .value=${this.noteText}
            @input=${(e: Event) =>
              (this.noteText = (e.target as HTMLInputElement).value)}
          ></wa-textarea>
          <div slot="footer">
            <wa-button
              variant="neutral"
              @click=${() => this._closeNoteDialog()}
            >
              Cancel
            </wa-button>
            <wa-button
              variant="brand"
              ?loading=${this.savingNote}
              ?disabled=${!this.noteText.trim()}
              @click=${() => this._handleSaveNote()}
            >
              Save Note
            </wa-button>
          </div>
        </wa-dialog>

      </app-shell>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "home-page": HomePage;
  }
}
